/**
 * <ImageAttachment> — the picked-image(s) preview card (molecule, L0.1).
 *
 * One of the three focused attachment previews (with `<PDFAttachment>` / `<LinkAttachment>`) that
 * replaced the former monolithic `AttachmentPreview`. A tinted `<Card>` with a title and either a single
 * large `<MediaThumbnail>` or a `<HorizontalList>` "track" of thumbnails, each with a trash affordance.
 * Tapping the card fires `onOpen` (open the viewer).
 *
 * Une pièce dont le `mimeType` est une vidéo prend la vignette de la vidéo (`<MediaThumbnail video>`),
 * et la carte s'intitule alors « Pièces jointes ». Sans vidéo, la carte d'avant.
 */
import React from "react"

import { Card } from "../Card"
import { HorizontalList } from "../../atoms/HorizontalList"
import { MediaThumbnail } from "../../atoms/MediaThumbnail"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"

type PreviewImage = { uri: string; width?: number; height?: number; mimeType?: string }

const isVideo = (image: PreviewImage) => image.mimeType?.startsWith("video/") === true

export type ImageAttachmentProps = {
    images: PreviewImage[]
    onDeleteImage: (index: number) => void
    onOpen?: () => void
    testID?: string
}

export function ImageAttachment(props: ImageAttachmentProps) {
    const { images, onDeleteImage, onOpen, testID } = props
    const title = images.some(isVideo) ? "Pièces jointes" : images.length > 1 ? "Images" : "Image"

    return (
        <Card background="primary-soft" p="5" pressable={onOpen != null} onPress={onOpen} testID={testID}>
            <Stack gap="3">
                <Text size="text-lg" weight="bold" color="primary">
                    {title}
                </Text>

                {images.length === 1 ? (
                    <MediaThumbnail
                        uri={images[0].uri}
                        size={160}
                        video={isVideo(images[0])}
                        onDelete={() => onDeleteImage(0)}
                    />
                ) : (
                    <HorizontalList padding="3">
                        {images.map((image, index) => (
                            <MediaThumbnail
                                key={`${image.uri}-${index}`}
                                uri={image.uri}
                                size={100}
                                video={isVideo(image)}
                                onDelete={() => onDeleteImage(index)}
                            />
                        ))}
                    </HorizontalList>
                )}
            </Stack>
        </Card>
    )
}
